/**
 * Bucket hooks — list/get-settings/create/delete/empty plus per-bucket setting
 * writes (versioning, public-access, tags, policy). All scoped by the
 * provisioned `serviceId` (the connection is resolved server-side from it).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  serviceApiV3,
  unwrap,
  storageBase,
  s3Keys,
  type Bucket,
  type BucketSettings,
} from './_shared';

/** List buckets for a service. */
export function useBuckets(serviceId: string | undefined) {
  return useQuery<Bucket[]>({
    queryKey: s3Keys.buckets(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 15_000,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${storageBase(serviceId!)}/buckets`);
      const data = unwrap<{ buckets?: Bucket[] }>(res);
      return data?.buckets ?? [];
    },
  });
}

/** Result of a global bucket-name availability check. */
export interface BucketNameCheck {
  available: boolean;
  reason?: 'invalid' | 'owned' | 'taken';
}

/**
 * Check whether a bucket name is free to create. Wasabi/S3 names are globally
 * unique, so this HEADs the name server-side. Caller gates `enabled` on a valid,
 * debounced name to avoid a request per keystroke.
 */
export function useBucketNameCheck(serviceId: string, name: string, enabled: boolean) {
  return useQuery<BucketNameCheck>({
    queryKey: [...s3Keys.buckets(serviceId), 'available', name],
    enabled: enabled && !!name,
    staleTime: 30_000,
    retry: false,
    queryFn: async () => {
      const res = await serviceApiV3.get(
        `${storageBase(serviceId)}/buckets/${encodeURIComponent(name)}/available`,
      );
      return unwrap<BucketNameCheck>(res);
    },
  });
}

/** Best-effort settings for one bucket (versioning/object-lock/tags/policy). */
export function useBucket(serviceId: string | undefined, bucket: string | undefined) {
  return useQuery<BucketSettings>({
    queryKey: s3Keys.bucket(serviceId ?? '', bucket ?? ''),
    enabled: !!serviceId && !!bucket,
    queryFn: async () =>
      unwrap<BucketSettings>(
        await serviceApiV3.get(`${storageBase(serviceId!)}/buckets/${bucket}`),
      ),
  });
}

export interface CreateBucketInput {
  name: string;
  region: string;
  versioning?: boolean;
  object_lock?: boolean;
}

export function useCreateBucket(
  serviceId: string,
  options?: UseMutationOptions<void, Error, CreateBucketInput>,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, CreateBucketInput>({
    mutationFn: async (input) => {
      await serviceApiV3.post(`${storageBase(serviceId)}/buckets`, input);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: s3Keys.buckets(serviceId) });
      qc.invalidateQueries({ queryKey: s3Keys.overview(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteBucket(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (bucket) => {
      await serviceApiV3.delete(`${storageBase(serviceId)}/buckets/${bucket}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.buckets(serviceId) });
      qc.invalidateQueries({ queryKey: s3Keys.overview(serviceId) });
    },
  });
}

/**
 * Rename a bucket. Only Wasabi implements a native rename; other providers
 * answer 501 and the caller surfaces that as "not supported". On success the
 * old name is gone, so we drop the whole bucket list + overview caches.
 */
export function useRenameBucket(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { bucket: string; newName: string }>({
    mutationFn: async ({ bucket, newName }) => {
      await serviceApiV3.post(
        `${storageBase(serviceId)}/buckets/${bucket}/rename`,
        { new_name: newName },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.buckets(serviceId) });
      qc.invalidateQueries({ queryKey: s3Keys.overview(serviceId) });
    },
  });
}

/** Turn versioning on/off for a bucket. */
export function useSetVersioning(serviceId: string, bucket: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, boolean>({
    mutationFn: async (enabled) => {
      await serviceApiV3.put(
        `${storageBase(serviceId)}/buckets/${bucket}/versioning`,
        { enabled },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.bucket(serviceId, bucket) });
    },
  });
}

/** Toggle the "block all public access" guard. `block: true` keeps it private. */
export function useSetPublicAccess(serviceId: string, bucket: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, boolean>({
    mutationFn: async (block) => {
      await serviceApiV3.put(
        `${storageBase(serviceId)}/buckets/${bucket}/public-access`,
        { block },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.bucket(serviceId, bucket) });
    },
  });
}

/** Replace the bucket's tag set (send the full map; an empty map clears tags). */
export function useSetBucketTags(serviceId: string, bucket: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, Record<string, string>>({
    mutationFn: async (tags) => {
      await serviceApiV3.put(
        `${storageBase(serviceId)}/buckets/${bucket}/tags`,
        { tags },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.bucket(serviceId, bucket) });
    },
  });
}

/** Set or clear the bucket policy — an empty string removes the policy. */
export function useSetBucketPolicy(serviceId: string, bucket: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (policy) => {
      await serviceApiV3.put(
        `${storageBase(serviceId)}/buckets/${bucket}/policy`,
        { policy },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.bucket(serviceId, bucket) });
    },
  });
}

/** Delete every object in a bucket (batched server-side) without removing it. */
export function useEmptyBucket(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (bucket) => {
      await serviceApiV3.post(`${storageBase(serviceId)}/buckets/${bucket}/empty`);
    },
    onSuccess: (_d, bucket) => {
      qc.invalidateQueries({ queryKey: s3Keys.buckets(serviceId) });
      qc.invalidateQueries({ queryKey: s3Keys.overview(serviceId) });
      qc.invalidateQueries({
        queryKey: ['s3', serviceId, 'bucket', bucket, 'objects'],
      });
    },
  });
}
